import React from 'react'
import { matchPath, matchRoutes, Navigate, Outlet as OutletBase, useLocation } from 'react-router'

import AnimatedLoader from '@/atoms/AnimatedLoader/AnimatedLoader'
import { CenteredAbsolute } from '@/atoms/GenericComponents'
import { useIsLoggedIn } from '@/atoms/PrivateRoute/useIsLoggedIn'
import { TabPanel } from '@/atoms/Tabs/TabPanel'
import { isLaunchedAsStandaloneApp } from '@/lib/mobile-app'
import { useStoreValue } from '@/lib/react-hooks/generic-hooks'
import { loginRoute } from '@/lib/route-utils'
import { Routes } from '@/lib/routes'
import { isCompanyContactPersonUser } from '@/lib/user-utils'
import AppBarBase from '@/organisms/AppBar/AppBar'
import { InactiveCompanyAccountPage } from '@/pages/InactiveCompanyAccount'
import { isSupportUser } from '@/travelsuit/my-user-roles'

export function PageLayout() {
	const location = useLocation()

	const { internalUser, company, user_can_operate } = useStoreValue(({ auth, myCompany }) => ({
		internalUser: auth.get('internalUser'),
		company: myCompany,
		user_can_operate: auth.get('user_can_operate'),
	}))

	const isLoggedIn = useIsLoggedIn()

	if (!isLoggedIn) {
		return <Navigate to={loginRoute()} />
	}

	if (!company) {
		return (
			<CenteredAbsolute>
				<AnimatedLoader />
			</CenteredAbsolute>
		)
	}

	const currentRoute = location.pathname as Routes

	const isCreditCard3DSCallback = !!matchRoutes(
		[Routes.CreditCardCallbackVerification, Routes.WordlineOrderCallbackVerification].map((route) => ({ path: route })),
		currentRoute,
	)

	if (isCreditCard3DSCallback) {
		return <Outlet />
	}

	const userCanOperate = company.is_after_onboarding || user_can_operate
	const isOnboardingRoute = !!matchRoutes(
		[Routes.CreateCompanyInvoices, Routes.OnboardingConfirmation].map((route) => ({ path: route })),
		currentRoute,
	)
	const isSupportRoute = currentRoute === Routes.Support

	if (!userCanOperate && isLaunchedAsStandaloneApp()) {
		return <InactiveCompanyAccountPage />
	}

	if (!userCanOperate && !isCompanyContactPersonUser(internalUser, company) && !isOnboardingRoute && !isSupportUser()) {
		return <Navigate to={Routes.OnboardingConfirmation} />
	}

	if (!userCanOperate && !isOnboardingRoute && !isSupportRoute) {
		return <Navigate to={Routes.CreateCompanyInvoices} />
	}

	if (userCanOperate && isOnboardingRoute) {
		return <Navigate to={Routes.Home} />
	}

	return <Outlet />
}

function Outlet() {
	const appBar = <AppBar />
	const outlet = <OutletBase />

	return (
		<>
			{appBar}
			{appBar ? <TabPanel id="app-bar">{outlet}</TabPanel> : outlet}
		</>
	)
}

function AppBar() {
	const { pathname } = useLocation()
	const isCallbackVerificationPath = !!matchPath(
		{
			path: Routes.CallbackVerification,
		},
		pathname,
	)

	return pathname !== Routes.Login && !isCallbackVerificationPath ? <AppBarBase route={pathname} /> : null
}
